/**
 * Secure Hybrid Supabase Storage Adapter
 *
 * TEMPORARY FIX: Using localStorage for all storage to fix authentication
 * This is a temporary solution while we resolve the async storage issues.
 *
 * TODO: Once auth is working, implement proper secure storage:
 * - PKCE tokens → localStorage (OK, they're temporary)
 * - Session tokens → Encrypted localStorage or secure cookies
 * - Add encryption layer for sensitive data in localStorage
 */

import type { SupportedStorage } from '@supabase/supabase-js'

// In-memory cache for performance
const memoryCache = new Map<string, string>()

class SecureHybridStorageAdapter implements SupportedStorage {

  /**
   * Get item - TEMPORARY: Using localStorage for everything
   */
  getItem(key: string): string | null {
    const timestamp = Date.now()

    // TEMPORARY: Use localStorage for everything
    // First check memory cache for performance
    const cachedValue = memoryCache.get(key)
    if (cachedValue) {
      console.log('🔍 [SecureHybridStorage] getItem from memory cache:', {
        key,
        hasValue: true,
        valueLength: cachedValue.length,
        elapsed: Date.now() - timestamp
      })
      return cachedValue
    }

    // Fall back to localStorage with error handling
    try {
      const value = window.localStorage.getItem(key)

      console.log('📦 [SecureHybridStorage] getItem from localStorage:', {
        key,
        hasValue: !!value,
        valueLength: value?.length || 0,
        elapsed: Date.now() - timestamp
      })

      // Cache in memory for performance
      if (value) {
        memoryCache.set(key, value)
      }

      return value
    } catch (error) {
      console.error('❌ [SecureHybridStorage] localStorage.getItem failed:', {
        key,
        error: error instanceof Error ? error.message : String(error),
        elapsed: Date.now() - timestamp
      })
      return null
    }
  }

  /**
   * Set item - TEMPORARY: Using localStorage for everything
   */
  setItem(key: string, value: string): void {
    const timestamp = Date.now()

    console.log('📝 [SecureHybridStorage] setItem:', {
      key,
      storage: 'localStorage+memory',
      valueLength: value.length,
      timestamp
    })

    // Always store in memory cache first
    memoryCache.set(key, value)

    // Try to store in localStorage with error handling
    try {
      window.localStorage.setItem(key, value)
      console.log('✅ [SecureHybridStorage] setItem COMPLETED:', {
        key,
        elapsed: Date.now() - timestamp
      })
    } catch (error) {
      console.error('❌ [SecureHybridStorage] localStorage.setItem failed:', {
        key,
        error: error instanceof Error ? error.message : String(error),
        elapsed: Date.now() - timestamp
      })
      // Data is still in memory cache, so operation partially succeeded
    }
  }

  /**
   * Remove item - TEMPORARY: Using localStorage for everything
   */
  removeItem(key: string): void {
    const timestamp = Date.now()

    console.log('🗑️ [SecureHybridStorage] removeItem:', {
      key,
      storage: 'localStorage+memory',
      timestamp
    })

    // Always remove from memory cache
    memoryCache.delete(key)

    // Try to remove from localStorage with error handling
    try {
      window.localStorage.removeItem(key)
      console.log('✅ [SecureHybridStorage] removeItem COMPLETED:', {
        key,
        elapsed: Date.now() - timestamp
      })
    } catch (error) {
      console.error('❌ [SecureHybridStorage] localStorage.removeItem failed:', {
        key,
        error: error instanceof Error ? error.message : String(error),
        elapsed: Date.now() - timestamp
      })
      // Key is still removed from memory cache, so operation partially succeeded
    }
  }
}

export const secureHybridStorage = new SecureHybridStorageAdapter()